import React from 'react';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { OpenInNew } from '@material-ui/icons';
import './home.css';

const translate = (key: string) => {
    if (key === 'home.contact') {
        return 'Contact';
    }
    if (key === 'home.privacy') {
        return 'Privacy Policy';
    }
    if (key === 'home.terms') {
        return 'Terms of Use';
    }
    if (key === 'home.disclaimer') {
        return ' Fighting for Healthcare, Fighting for You.';
    }
    return key;
};

const Home = () => {
    return (
        <div className="page-wrapper">
            {/* Header */}
            <header className="site-header">
                <Container>
                    <Typography variant="h4" component="h1" className="header-title">
                        Tom Kean Jr. and the Healthcare Crisis
                    </Typography>
                    <nav className="header-nav">
                        {/* <Button href="/contact" className="nav-button">
                            {translate('home.contact')}
                        </Button> */}
                    </nav>
                </Container>
            </header>

            {/* Main Content */}
            <main className="main-content">
                {/* Hero Section */}
                <section className="hero-section">
                    <Container>
                        <Typography variant="h5" component="p" className="hero-subtext">
                            Your Healthcare Is On the Line. Tom Kean Jr. voted to cut ACA subsidies and Medicaid funding. Families in NJ-7 are paying the price.
                        </Typography>
                    </Container>
                </section>

                {/* ACA Subsidy Cuts Section */}
                <section className="aca-section">
                    <Container>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography variant="h3" component="h3" className="section-title">
                                    ACA Subsidy Cuts
                                </Typography>
                                <Typography variant="body1" component="p" className="section-text">
                                    In 2025, ACA subsidies expired. Premiums jumped 174% for 2026. Tom Kean Jr. voted with his party to eliminate subsidies.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box className="chart-container">
                                    <div className="premium-chart">
                                        <Typography variant="h6" className="chart-title">Premium Increase</Typography>
                                        <div className="chart-bars">
                                            <div className="chart-bar-group">
                                                <div className="chart-bar bar-2025" />
                                                <Typography variant="caption" className="bar-label">2025</Typography>
                                                <Typography variant="caption" className="bar-value">Baseline</Typography>
                                            </div>
                                            <div className="chart-bar-group">
                                                <div className="chart-bar bar-2026" />
                                                <Typography variant="caption" className="bar-label">2026</Typography>
                                                <Typography variant="caption" className="bar-value">+174%</Typography>
                                            </div>
                                        </div>
                                    </div>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </section>

                {/* Voting Record Section */}
                <section className="voting-record-section">
                    <Container>
                        <Typography variant="h3" component="h3" className="section-title" style={{ textAlign: 'center', marginBottom: '2rem' }}>
                            The Record Is Clear
                        </Typography>
                        <Box display="flex" justifyContent="center" mb={3}>
                            <img 
                                src="/kean-voting-record.webp" 
                                alt="Tom Kean Jr. voted YEA on H.R.1 - One Big Beautiful Bill Act, cutting ACA subsidies and Medicaid funding"
                                style={{ 
                                    maxWidth: '100%', 
                                    height: 'auto',
                                    borderRadius: '10px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }}
                            />
                        </Box>
                        <Box display="flex" justifyContent="center" flexWrap="wrap" style={{ gap: '1rem' }}>
                            <Button 
                                variant="contained" 
                                color="primary"
                                href="https://clerk.house.gov/Votes/2025145"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <OpenInNew style={{ marginRight: '8px' }} />
                                View Kean's Vote
                            </Button>
                            <Button 
                                variant="contained" 
                                color="secondary"
                                href="https://www.congress.gov/bill/119th-congress/house-bill/1"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <OpenInNew style={{ marginRight: '8px' }} />
                                Read the Bill (H.R.1)
                            </Button>
                        </Box>
                    </Container>
                </section>

                {/* Medicaid Cuts Section */}
                <section className="medicaid-section">
                    <Container>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Box className="medicaid-visual">
                                    <div className="medicaid-overlay">
                                        <Typography variant="h4" className="overlay-text">
                                            Medicaid = Lifeline
                                        </Typography>
                                    </div>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h3" component="h3" className="section-title">
                                    Medicaid Cuts
                                </Typography>
                                <Typography variant="body1" component="p" className="section-text">
                                    Kean supported Medicaid cuts that threaten seniors, children, and people with disabilities. These cuts reduce access to doctors, hospitals, and long‑term care.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Container>
                </section>

                {/* Medicare Cuts Section */}
                <section className="medicare-section" id="medicare">
                    <Container>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography variant="h3" component="h3" className="section-title">
                                    Tom Kean Jr. and Medicare Cuts
                                </Typography>
                                <Typography variant="body1" component="p" className="section-subtext">
                                    Republican proposals include hundreds of billions in Medicare reductions. Tom Kean Jr. has aligned with these efforts, threatening seniors' healthcare.
                                </Typography>
                                <ul className="medicare-bullets">
                                    <li>
                                        <Typography variant="body1" component="span" className="section-text">
                                            GOP budget drafts propose $479 billion in Medicare cuts (payment structures, eligibility changes).
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography variant="body1" component="span" className="section-text">
                                            The "One Big Beautiful Bill Act" included provisions restricting Medicare access.
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography variant="body1" component="span" className="section-text">
                                            Seniors in NJ‑7 rely on Medicare for hospital visits, prescriptions, and long‑term care.
                                        </Typography>
                                    </li>
                                </ul>
                                <Button 
                                    variant="contained" 
                                    className="medicare-cta"
                                    href="https://www.onenj7.org/action"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Protect Medicare — Learn More / Get Involved
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box 
                                    className="medicare-visual" 
                                    role="img" 
                                    aria-label="Medicare is a lifeline for seniors - image showing the importance of Medicare for elderly healthcare"
                                >
                                    <div className="medicare-overlay">
                                        <Typography variant="h4" className="overlay-text">
                                            Medicare = Lifeline
                                        </Typography>
                                    </div>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </section>

                {/* Accountability Section */}
                <section className="accountability-section" id="accountability">
                    <Container>
                        <Card elevation={0} className="accountability-card">
                            <CardContent>
                                <Typography variant="h3" component="h3" className="section-title accountability-title">
                                    Hold Him Accountable
                                </Typography>
                                <Typography variant="body1" component="p" className="section-text accountability-text">
                                    Tom Kean Jr.'s record is clear: higher costs, less care, more hardship. NJ‑7 deserves better.
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    className="accountability-cta"
                                    href="https://www.mobilize.us/indivisible/event/771456/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Join #FindingKeanFridays
                                </Button>
                            </CardContent>
                        </Card>
                    </Container>
                </section>
            </main>

            {/* Footer */}
            <footer className="site-footer">
                <Container>
                    <Paper elevation={0} className="footer-disclaimer">
                        <Typography variant="body2" className="disclaimer-text">
                            {translate('home.disclaimer')}
                        </Typography>
                    </Paper>
                    <div className="footer-links">
                        <Button href="/terms-of-use.html" className="footer-link">
                            <OpenInNew style={{ paddingRight: '0.5em', fontSize: '1rem' }} />
                            {translate('home.terms')}
                        </Button>
                        <Button href="/privacy-policy.html" className="footer-link">
                            <OpenInNew style={{ paddingRight: '0.5em', fontSize: '1rem' }} />
                            {translate('home.privacy')}
                        </Button>
                    </div>
                </Container>
            </footer>
        </div>
    );
};

export default Home;
